// src/oura/delivery/DeliverableDrawer.tsx
//
// One deliverable's drawer. It leads with the next step; the other items sit
// under it by state, each with its square, and pages show only when an item
// is opened. One action is pinned in the foot, the one the deliverable needs
// now (first match wins), and after it the drawer offers the next
// deliverable. Staff do the work; Oura's check or records prove it.
//
// Behind the Docs write switch (v1.3, H31): "Create the doc" opens Create a
// doc filled in for this code (`onCreateDoc`), "Read it, then share" opens
// the unshared doc, and the approved PDF shows once a person filed it
// (DocStepParts.tsx).
//
// Phase 3 (v1.3, H32 to H39), behind Delivery and the Docs write switch: an
// opened item, and the Next block, show the work people did on it
// (WorkParts.tsx); Close by hand takes a proof file; an AI readers item is
// tested, not ticked by hand.

import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import type { Deliverable, DeliveryFailure, DeliveryItem, DeliveryPayload } from "@service/aeo/delivery";
import { OUTLETS_NOT_CONFIRMED, type PlanSection } from "../components/plan/planModel";
import {
    addLivePage,
    checkNow,
    closeByHand,
    confirmProof,
    DELIVERY_PROOF_FILE_ON,
    MAY_HAVE_POSTED,
    dayLabel,
    deliveryFailure,
    markFixed,
    noAction,
    reopenDeliverable,
    shortDay,
    workingDayText,
    testAiReaders,
    tickItem,
    unmarkFixed,
    untickItem,
} from "@service/aeo/delivery";
import { recordView } from "@service/aeo/deliveryRules";
import { cn } from "../lib/cn";
import { DrawerBlock, DrawerFold, DrawerFoot, DrawerHead, FamilyChip, PillButton, Tag, TYPE, type DrawerDone, type DrawerFootMessage } from "../ui";
import { familyOf } from "../components/plan/planModel";
import { CLIENT_WORD_TONE, GROUP_STATUS, GROUP_TONE, instantDay, plural } from "./format";
import { ItemRow, ItemSection, PageForm, PagesList, ProofForm, ReasonForm, TickForm } from "./drawerParts";
import { ApprovedPdfBlock, DocStepBlock, createPrefillOf } from "./DocStepParts";
import type { CreatePrefill } from "../components/docs/create/createModel";
import { ItemWork } from "./WorkParts";
import { isReaderTest } from "./workModel";
import { DELIVERY_WORK_ON } from "./workFlag";
import { useWritesPaused } from "@context/maintenance";

type Mode =
    | null
    | { kind: "tick"; itemId: string }
    | { kind: "page"; itemId: string }
    | { kind: "close"; itemId: string }
    | { kind: "noaction"; itemId: string; page: string }
    | { kind: "reopen" }
    | { kind: "proof" }
    | { kind: "already_fine" };

export interface DeliverableSheetProps {
    businessId: string;
    d: Deliverable;
    payload: DeliveryPayload;
    /** Builds the done line after an action moved the deliverable out of its group. */
    makeDone: (said: string) => DrawerDone | null;
    onApplied: (d: Deliverable) => void;
    onOpenDoc: (docId: string) => void;
    /** Another staff view of this business, with an optional search ("?issue=no_schema"). */
    onGoTo: (view: string, search?: string) => void;
    onOpenPlan: (section?: PlanSection) => void;
    onNextDeliverable: () => void;
    /** Create a doc, filled in for this deliverable (behind the Docs write switch). Absent: the Client tab. */
    onCreateDoc?: (prefill: CreatePrefill) => void;
}

/**
 * The document in a few words, with its wait when Docs has read it: "Waiting on
 * the client, 4 working days · 1 day late", "Client replied, 2 working days".
 */
function documentFact(doc: NonNullable<Deliverable["document"]>): string {
    const days = doc.waitingWorkingDays;
    // Same rule as the list's own wait: a wait that has not crossed a working
    // day reads "under 1 working day", never "0 working days" (#3999).
    const wait = doc.waitingOn && days != null ? `${doc.waitingOn === "client" ? "Waiting on client" : "Waiting on us"}, ${workingDayText(days)}` : doc.state;
    if (doc.late && doc.lateWorkingDays) return `${wait} · ${plural(doc.lateWorkingDays, "day")} late`;
    if (doc.replyBy && doc.waitingOn === "client") return `${wait} · reply by ${shortDay(doc.replyBy)}`;
    return wait;
}

function failText(f: DeliveryFailure): string {
    if (f.reason === "rejected" && f.message) return f.message;
    if (f.reason === "notServed") return "Delivery is not available here yet.";
    if (f.reason === "forbidden") return "Only staff can do this.";
    return "That did not save. Try again.";
}

export function DeliverableSheet({ businessId, d, payload, makeDone, onApplied, onOpenDoc, onGoTo, onOpenPlan, onNextDeliverable, onCreateDoc }: DeliverableSheetProps) {
    const [mode, setMode] = useState<Mode>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** The server's refusal code for the last failure: one of them offers a way out. */
    const [errorCode, setErrorCode] = useState<string | null>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const [done, setDone] = useState<DrawerDone | null>(null);
    const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
    const [showNextPages, setShowNextPages] = useState(false);
    const [lastMarked, setLastMarked] = useState<string | null>(null);
    const writesPaused = useWritesPaused();

    const next =d.next ? d.items.find((i) => i.id === d.next?.itemId) ?? null : null;
    const running = payload.lastCheck.running;

    // When a check this drawer started finishes, say what it read.
    const wasRunning = useRef(running);
    useEffect(() => {
        if (wasRunning.current && !running) setMessage({ tone: "success", text: "Checked just now. The list shows what Oura read." });
        wasRunning.current = running;
    }, [running]);

    const sections = useMemo(() => {
        const rest = d.items.filter((i) => i.id !== next?.id);
        return {
            todo: rest.filter((i) => i.state === "todo" || (i.clientRepliedAt && i.state !== "done")),
            oura: rest.filter((i) => i.state === "waiting" && i.waitingOn !== "client"),
            // A replied item is ours now, so it sits under Also to do, not here too.
            client: rest.filter((i) => i.state === "waiting" && i.waitingOn === "client" && !i.clientRepliedAt),
            done: rest.filter((i) => i.state === "done"),
        };
    }, [d.items, next?.id]);

    const run = async (said: string, write: () => Promise<{ ok: true; data: Deliverable; note?: string } | DeliveryFailure>, after?: (d2: Deliverable) => void) => {
        setBusy(true);
        setError(null);
        setErrorCode(null);
        setMessage(null);
        // Each write starts clean: an earlier done line or Undo never carries over to this one.
        setDone(null);
        setLastMarked(null);
        const before = d.group;
        // Busy always clears: a write that throws reads as one that did not go through.
        let r: Awaited<ReturnType<typeof write>>;
        try {
            r = await write();
        } catch {
            r = deliveryFailure("network");
        } finally {
            setBusy(false);
        }
        if (!r.ok) {
            // A Live since reply that may have posted says so in its own block, with the doc to check: not twice.
            setError(r.code === MAY_HAVE_POSTED ? null : failText(r));
            setErrorCode(r.code ?? null);
            return false;
        }
        setMode(null);
        onApplied(r.data);
        after?.(r.data);
        // The write went through but the screen has not read it back yet: say so, in its own words.
        if (r.note) {
            setMessage({ tone: "success", text: r.note });
            return true;
        }
        if (r.data.group !== before) {
            const doneLine = makeDone(said);
            if (doneLine) {
                setDone(doneLine);
                return true;
            }
        }
        setMessage({ tone: "success", text: `${said}.` });
        return true;
    };

    const doMarkFixed = (item: DeliveryItem) =>
        run(`${item.label} marked fixed. Oura re-checks tonight`, () => markFixed(businessId, item.id), () => setLastMarked(item.id));

    const doCheckNow = async () => {
        if (busy || running) return;
        setBusy(true);
        setError(null);
        setDone(null);
        const r = await checkNow(businessId);
        setBusy(false);
        if (!r.ok) setError(failText(r));
        else setMessage({ tone: "info", text: "Oura is checking the marked pages now. About a minute on a 600-page site." });
    };

    /* The pinned action */

    const act = () => {
        const a = d.mainAction;
        switch (a.kind) {
            case "confirm_key_pages":
                onOpenPlan("key-pages");
                return;
            case "mark_fixed": {
                const item = d.items.find((i) => i.id === a.itemId);
                if (item) void doMarkFixed(item);
                return;
            }
            case "open_doc":
                if (a.docId) onOpenDoc(a.docId);
                return;
            case "link_doc":
                onGoTo("reviews");
                return;
            case "create_doc":
                // v1.3 (H31), offered only behind the Docs write switch: read the unshared doc, or create it here, filled in.
                if (a.docId) onOpenDoc(a.docId);
                else if (onCreateDoc) onCreateDoc(createPrefillOf(d, a));
                else onGoTo("reviews");
                return;
            case "add_live_page":
                if (a.itemId) setMode({ kind: "page", itemId: a.itemId });
                return;
            case "tick": {
                // H38: the AI readers item closes on the reader test's proof, not a hand tick.
                const item = d.items.find((i) => i.id === a.itemId);
                if (isReaderTest(item)) void run("AI readers tested", () => testAiReaders(businessId));
                else if (a.itemId) setMode({ kind: "tick", itemId: a.itemId });
                return;
            }
            case "check_now":
                void doCheckNow();
                return;
            case "close_by_hand":
                if (a.itemId) setMode({ kind: "close", itemId: a.itemId });
                return;
            case "go_to":
                onGoTo(a.to ?? "dashboard");
                return;
            default:
                onNextDeliverable();
        }
    };

    const ourMove = d.group === "needs_us" || d.mainAction.kind === "confirm_key_pages" || d.mainAction.kind === "create_doc";
    const readerTestPinned = d.mainAction.kind === "tick" && isReaderTest(d.items.find((i) => i.id === d.mainAction.itemId));
    // The pinned action writes, or opens a form that writes; a doc or a page only opens, so it stays live.
    const firstWrites =
        ["mark_fixed", "check_now", "add_live_page", "close_by_hand", "tick"].includes(d.mainAction.kind) ||
        (d.mainAction.kind === "create_doc" && !d.mainAction.docId) ||
        readerTestPinned;
    const firstLabel = d.mainAction.kind === "check_now" && running ? "Checking now" : readerTestPinned ? "Test the AI readers" : d.mainAction.label;
    const work = (item: DeliveryItem) => (
        <ItemWork item={item} deliverable={d} businessId={businessId} businessName={payload.businessName} busy={busy} onWrite={(said, write) => run(said, write)} onOpenDoc={onOpenDoc} onGoTo={onGoTo} />
    );
    const secondary =
        d.document && d.mainAction.kind !== "open_doc" ? (
            <PillButton onClick={() => onOpenDoc(d.document!.docId)}>Open the doc</PillButton>
        ) : next?.kind === "check" && d.mainAction.kind === "mark_fixed" ? (
            <PillButton onClick={() => onGoTo("setup")}>Open Site issues</PillButton>
        ) : next?.kind === "page" ? (
            <PillButton onClick={() => onGoTo("pages")}>Open Pages</PillButton>
        ) : undefined;

    /* An item's own controls, when it is opened */

    const itemActions = (item: DeliveryItem) => {
        const buttons: ReactElement[] = [];
        if (item.kind === "check" && item.state !== "done" && item.pages.some((p) => p.state === "todo")) {
            buttons.push(
                <PillButton key="mark" size="sm" variant="primary" busy={busy} writes onClick={() => void doMarkFixed(item)}>
                    Mark fixed on all its pages
                </PillButton>,
            );
        }
        if (item.kind === "check" && item.pages.some((p) => p.state === "waiting" && p.markedFixedAt)) {
            buttons.push(
                <PillButton key="unmark" size="sm" disabled={busy} writes onClick={() => void run(`Undid Mark fixed on ${item.label.toLowerCase()}`, () => unmarkFixed(businessId, item.id))}>
                    Undo Mark fixed
                </PillButton>,
            );
        }
        if (item.kind === "check" && item.state !== "done" && item.pages.some((p) => p.state === "not_read" || p.state === "todo")) {
            buttons.push(
                <PillButton key="close" size="sm" disabled={busy} writes onClick={() => setMode({ kind: "close", itemId: item.id })}>
                    Close by hand
                </PillButton>,
            );
        }
        if (item.state === "waiting" && item.waitingOn !== "client") {
            buttons.push(
                <PillButton key="check" size="sm" busy={busy && running} disabled={busy || running} writes onClick={() => void doCheckNow()}>
                    {running ? "Checking now" : "Check now"}
                </PillButton>,
            );
        }
        if (item.kind === "page" && item.state === "todo") {
            buttons.push(
                <PillButton key="page" size="sm" variant="primary" disabled={busy} writes onClick={() => setMode({ kind: "page", itemId: item.id })}>
                    Add a live page
                </PillButton>,
            );
        }
        if (item.kind === "confirm" && item.state !== "done" && !isReaderTest(item)) {
            buttons.push(
                <PillButton key="tick" size="sm" variant="primary" disabled={busy} writes onClick={() => setMode({ kind: "tick", itemId: item.id })}>
                    Tick, with a link or a note
                </PillButton>,
            );
        }
        if (item.kind === "confirm" && item.state === "done" && !item.doesNotApply) {
            buttons.push(
                <PillButton key="untick" size="sm" disabled={busy} writes onClick={() => void run(`Undid the tick on ${item.label.toLowerCase()}`, () => untickItem(businessId, item.id))}>
                    Undo the tick
                </PillButton>,
            );
        }
        if (item.kind === "record" && item.state !== "done") {
            buttons.push(
                <PillButton key="go" size="sm" disabled={busy} onClick={() => onGoTo(recordView(item))}>
                    {item.staffDo ?? "Open it"}
                </PillButton>,
            );
        }
        return (
            <>
                {item.pages.length > 0 && (
                    <PagesList item={item} busy={busy} onNoAction={(page) => setMode({ kind: "noaction", itemId: item.id, page })} />
                )}
                {work(item)}
                {buttons.length > 0 && <div className="flex flex-wrap items-center gap-2">{buttons}</div>}
            </>
        );
    };

    const toggle = (id: string) => setOpenItems((o) => ({ ...o, [id]: !o[id] }));
    const itemList = (items: DeliveryItem[]) =>
        items.map((item) => (
            <ItemRow key={item.id} item={item} isNext={false} open={Boolean(openItems[item.id])} onToggle={() => toggle(item.id)}>
                {itemActions(item)}
            </ItemRow>
        ));

    /**
     * "Confirm the outlet names first." used to be a dead end: the server
     * refuses every per-outlet tick on a 2-or-more-outlet contract until the
     * outlets are named, and nothing on this page offered the step (#3985).
     * The refusal now carries the way out of itself.
     */
    const shownError: ReactNode =
        error && errorCode === OUTLETS_NOT_CONFIRMED ? (
            <>
                {error}{" "}
                <button
                    type="button"
                    onClick={() => onOpenPlan("outlet-names")}
                    className="font-medium underline decoration-rose-300 underline-offset-2 hover:decoration-rose-600"
                >
                    Name the outlets on Plan
                </button>
            </>
        ) : (
            error
        );

    /* The form the current action needs */

    const modeItem = mode && "itemId" in mode ? d.items.find((i) => i.id === mode.itemId) ?? null : null;
    const form =
        mode?.kind === "tick" && modeItem ? (
            <TickForm
                label={modeItem.outlet ? `${modeItem.label}, ${modeItem.outlet}` : modeItem.label}
                busy={busy}
                error={shownError}
                onCancel={() => setMode(null)}
                onSubmit={(body) => void run(`${modeItem.label} ticked`, () => tickItem(businessId, modeItem.id, body))}
            />
        ) : mode?.kind === "page" && modeItem ? (
            <PageForm
                item={modeItem}
                busy={busy}
                error={shownError}
                onCancel={() => setMode(null)}
                onSubmit={(body) => void run("Page added. Oura reads it tonight", () => addLivePage(businessId, modeItem.id, body))}
            />
        ) : mode?.kind === "close" && modeItem ? (
            <ProofForm
                title={`Close by hand: ${modeItem.label}`}
                submit="Close by hand"
                hint="For a page Oura cannot read. It shows as Closed by hand, with your proof."
                withFile={DELIVERY_WORK_ON}
                busy={busy}
                error={shownError}
                onCancel={() => setMode(null)}
                onSubmit={(body) => void run(`${modeItem.label} closed by hand`, () => closeByHand(businessId, modeItem.id, body))}
            />
        ) : mode?.kind === "noaction" && modeItem ? (
            <ReasonForm
                title={`No action on ${mode.page}`}
                submit="Leave it as it is"
                placeholder="The page is an old promotion we keep on purpose"
                busy={busy}
                error={shownError}
                onCancel={() => setMode(null)}
                onSubmit={(reason) => void run(`No action on ${mode.page}`, () => noAction(businessId, modeItem.id, mode.page, reason))}
            />
        ) : mode?.kind === "proof" ? (
            <ProofForm
                title="Add what we did"
                submit="Save and mark done"
                hint="It shows as our work in the report, with your proof."
                withFile={DELIVERY_PROOF_FILE_ON}
                busy={busy}
                error={shownError}
                onCancel={() => setMode(null)}
                onSubmit={(body) => void run("Proof added, marked done", () => confirmProof(businessId, d.key, { kind: "work", ...body }))}
            />
        ) : mode?.kind === "already_fine" ? (
            <ReasonForm
                title="Already fine, no work needed"
                submit="Mark already fine"
                placeholder="The site already had this before we started"
                busy={busy}
                error={shownError}
                onCancel={() => setMode(null)}
                onSubmit={(reason) => void run("Marked already fine", () => confirmProof(businessId, d.key, { kind: "already_fine", note: reason || undefined }))}
            />
        ) : mode?.kind === "reopen" ? (
            <ReasonForm
                title="Reopen with a reason"
                submit="Reopen"
                placeholder="The client found the old number on Yelp"
                busy={busy}
                error={shownError}
                onCancel={() => setMode(null)}
                onSubmit={(reason) => void run("Reopened", () => reopenDeliverable(businessId, d.key, reason))}
            />
        ) : null;

    const late = Boolean(d.actor?.late);
    const statusClock = d.group === "done" ? (d.doneAt ? `done ${dayLabel(d.doneAt)}` : "done") : d.group === "later" ? (d.window ? `from ${dayLabel(d.window.start)}` : "not started") : d.due ? `due ${dayLabel(d.due)}` : "no date yet";

    return (
        <>
            <DrawerHead
                lead={<FamilyChip family={familyOf(d.code)}>{d.code}</FamilyChip>}
                title={d.label}
                chips={
                    <>
                        <Tag tone="orange">Month {d.month}</Tag>
                        {d.actor?.cameBack && <Tag tone="rose">Came back</Tag>}
                    </>
                }
                status={{ tone: d.passedWithoutWork ? "amber" : GROUP_TONE[d.group], label: d.passedWithoutWork ? "Needs proof" : GROUP_STATUS[d.group], clock: { text: statusClock, late, title: late ? `Was due ${dayLabel(d.due)}` : undefined } }}
            />

            <div className="space-y-3">
                <p className={cn(TYPE.tc, "m-0 text-gray-600")} data-drawer-head-line>
                    {d.head}
                </p>
                <p className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-1.5 text-gray-600")}>
                    <span>Client sees:</span>
                    <Tag tone={CLIENT_WORD_TONE[d.clientWord]}>{d.clientWord}</Tag>
                    <span className="text-gray-500">{d.clientLine.replace(/^(Done|In progress|Needs you|Not started)[^.]*\.\s?/, "") || null}</span>
                </p>

                {d.reopened && (
                    <DrawerBlock label="Reopened">
                        {d.reopened.reason} <span className="text-gray-500">· {d.reopened.by}, {instantDay(d.reopened.at)}</span>
                    </DrawerBlock>
                )}

                {d.passedWithoutWork && (
                    <section className="rounded-2xl bg-amber-50 p-4 ring-1 ring-amber-200" aria-label="Needs proof">
                        <p className={cn(TYPE.lab, "m-0 text-amber-800")}>Needs proof</p>
                        <p className={cn(TYPE.tb, "m-0 mt-1 font-medium text-gray-900")}>Oura's check passed {instantDay(d.passedWithoutWork.at)}, but nobody logged work on it.</p>
                        <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-600")}>It stays open until you add what we did, or say it was already fine. Already fine shows in the report as checked, not as our work.</p>
                    </section>
                )}

                {d.alreadyFine && (
                    <DrawerBlock label="Already fine">
                        Checked, not our work{d.alreadyFine.note ? `. ${d.alreadyFine.note}` : ""} <span className="text-gray-500">· {d.alreadyFine.by}, {instantDay(d.alreadyFine.at)}</span>
                    </DrawerBlock>
                )}

                {next && (
                    <section className="rounded-2xl bg-blue-50/70 p-4 ring-1 ring-blue-100" aria-label="Next">
                        <p className={cn(TYPE.lab, "m-0 text-blue-700")}>Next</p>
                        <p className={cn(TYPE.tb, "m-0 mt-1 font-medium text-gray-900")}>{d.next?.text}</p>
                        <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-600")}>
                            {next.label}
                            {next.cameBack ? " · came back" : ""}
                            {next.clientRepliedAt ? ` · the client replied ${instantDay(next.clientRepliedAt)}` : ""}
                        </p>
                        {next.pages.length > 0 && (
                            <div className="mt-2">
                                <button type="button" onClick={() => setShowNextPages((v) => !v)} className={cn(TYPE.tc, "font-medium text-blue-700 hover:underline")} aria-expanded={showNextPages}>
                                    {showNextPages ? "Hide the pages" : `Show the ${plural(next.pages.length, "page")}`}
                                </button>
                                {showNextPages && (
                                    <div className="mt-2 rounded-xl bg-white p-3">
                                        <PagesList item={next} busy={busy} onNoAction={(page) => setMode({ kind: "noaction", itemId: next.id, page })} />
                                    </div>
                                )}
                            </div>
                        )}
                        {DELIVERY_WORK_ON && <div className="mt-3 empty:hidden">{work(next)}</div>}
                        {next.kind === "check" && next.pages.some((p) => p.state === "not_read") && (
                            <button type="button" disabled={writesPaused} onClick={() => setMode({ kind: "close", itemId: next.id })} className={cn(TYPE.tc, "mt-2 block font-medium text-gray-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50")}>
                                Oura cannot read a page? Close it by hand
                            </button>
                        )}
                    </section>
                )}

                {form}

                <DocStepBlock d={d} onCreate={(p) => (onCreateDoc ? onCreateDoc(p) : onGoTo("reviews"))} onOpenDoc={onOpenDoc} />

                {sections.todo.length > 0 && <ItemSection title="Also to do" count={sections.todo.length}>{itemList(sections.todo)}</ItemSection>}
                {sections.oura.length > 0 && (
                    <ItemSection
                        title="Waiting on Oura's check"
                        count={sections.oura.length}
                        right={
                            <button type="button" disabled={busy || running || writesPaused} onClick={() => void doCheckNow()} className={cn(TYPE.tc, "font-medium text-blue-700 hover:underline disabled:text-gray-400 disabled:no-underline")}>
                                {running ? "Checking now" : "Check now"}
                            </button>
                        }
                    >
                        {itemList(sections.oura)}
                    </ItemSection>
                )}
                {sections.client.length > 0 && <ItemSection title="Waiting on client" count={sections.client.length}>{itemList(sections.client)}</ItemSection>}
                {sections.done.length > 0 && (
                    <DrawerFold id="staff-delivery.done" label="Done" note={plural(sections.done.length, "item")} remember>
                        <div>{itemList(sections.done)}</div>
                    </DrawerFold>
                )}

                <Extras d={d} />


                {d.document && (
                    <DrawerBlock label="Where the client answered">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <span className="min-w-0 flex-1 basis-40">{d.document.title}</span>
                            <button type="button" onClick={() => onOpenDoc(d.document!.docId)} className={cn(TYPE.tc, "shrink-0 font-medium text-blue-700 hover:underline")}>
                                Open the doc
                            </button>
                        </div>
                        {/* The wait on its own line, so the title never squeezes. */}
                        <p className={cn(TYPE.tc, "m-0 mt-0.5", d.document.late ? "font-medium text-rose-600" : "text-gray-500")}>{documentFact(d.document)}</p>
                    </DrawerBlock>
                )}

                <ApprovedPdfBlock businessId={businessId} d={d} />

                {d.history.length > 0 && (
                    <DrawerFold id="staff-delivery.history" label="History" note={plural(d.history.length, "entry", "entries")} remember>
                        <ol className="m-0 list-plain space-y-2 p-0">
                            {[...d.history].sort((a, b) => b.at.localeCompare(a.at)).map((h, i) => (
                                <li key={`${h.at}-${i}`} className={cn(TYPE.tc, "flex gap-3")}>
                                    <span className="w-14 shrink-0 tabular-nums text-gray-500">{instantDay(h.at)}</span>
                                    <span className="min-w-0 flex-1 text-gray-900">{h.text}</span>
                                </li>
                            ))}
                        </ol>
                    </DrawerFold>
                )}

                {d.group === "done" && !mode && (
                    <button type="button" disabled={writesPaused} onClick={() => setMode({ kind: "reopen" })} className={cn(TYPE.tc, "font-medium text-gray-500 hover:text-gray-900 hover:underline disabled:cursor-not-allowed disabled:opacity-50")}>
                        Reopen with a reason
                    </button>
                )}
            </div>

            <DrawerFoot
                message={
                    error && !mode
                        ? { tone: "error", text: error }
                        : message
                          ? {
                                tone: message.tone,
                                text:
                                    lastMarked && message.tone === "success" ? (
                                        <>
                                            {message.text}{" "}
                                            <button
                                                type="button"
                                                className="font-medium text-blue-700 hover:underline"
                                                disabled={writesPaused}
                                                onClick={() => {
                                                    const id = lastMarked;
                                                    setLastMarked(null);
                                                    void run("Undid Mark fixed", () => unmarkFixed(businessId, id));
                                                }}
                                            >
                                                Undo
                                            </button>
                                        </>
                                    ) : (
                                        message.text
                                    ),
                            }
                          : null
                }
                done={done}
                first={
                    mode ? undefined : d.passedWithoutWork ? (
                        <PillButton variant="primary" busy={busy} writes onClick={() => setMode({ kind: "proof" })}>
                            Add what we did
                        </PillButton>
                    ) : (
                        <PillButton variant={ourMove ? "primary" : "secondary"} busy={busy} writes={firstWrites} disabled={d.mainAction.kind === "check_now" && running} onClick={act}>
                            {firstLabel}
                        </PillButton>
                    )
                }
                second={
                    mode ? undefined : d.passedWithoutWork ? (
                        <PillButton disabled={busy} writes onClick={() => setMode({ kind: "already_fine" })}>
                            Already fine
                        </PillButton>
                    ) : (
                        secondary
                    )
                }
            />
        </>
    );
}

function Extras({ d }: { d: Deliverable }) {
    const x = d.extras;
    if (!x && !d.proofLine) return null;
    return (
        <>
            {d.group === "done" && d.proofLine && !d.alreadyFine && <DrawerBlock label="Proof">{d.proofLine}</DrawerBlock>}
            {x?.pagesSold != null && (d.code === "B1" || d.code === "G1") && (
                <DrawerBlock label="Pages sold">
                    {x.pagesSold} · {x.pagesLive ?? 0} live. Each page is a new one or an existing one improved, agreed with the client during the work.
                </DrawerBlock>
            )}
            {x?.inDocument && (
                <DrawerBlock label="In the document, not items">{x.inDocument}</DrawerBlock>
            )}
            {x?.aiNamedSinceLive && (
                <DrawerBlock label="AI naming since live">
                    {x.aiNamedSinceLive.named} of {x.aiNamedSinceLive.of} questions. <span className="text-gray-500">Impact only. It never closes a page.</span>
                </DrawerBlock>
            )}
            {x?.movedOut && x.movedOut.length > 0 && (
                <DrawerBlock label="Moved out">
                    {x.movedOut.map((m) => (
                        <p key={m.text} className="m-0">
                            {m.text}
                        </p>
                    ))}
                </DrawerBlock>
            )}
            {x?.dismissedSuggestions && x.dismissedSuggestions.length > 0 && (
                <DrawerBlock label="Suggestions dismissed">
                    {x.dismissedSuggestions.map((s) => (
                        <p key={s.text} className="m-0">
                            {s.text}. <span className="text-gray-500">Dismissed {instantDay(s.at)} by {s.by}, so it never became an item.</span>
                        </p>
                    ))}
                </DrawerBlock>
            )}
            {x?.notes && x.notes.length > 0 && (
                <DrawerBlock label="Notes">
                    {x.notes.map((n) => (
                        <p key={n.text} className="m-0">
                            “{n.text}” <span className="text-gray-500">{n.by}, {instantDay(n.at)}</span>
                        </p>
                    ))}
                </DrawerBlock>
            )}
        </>
    );
}
